import { Stack } from "@mui/material";
import { type FC, useCallback } from "react";
import {
	type ClientPortfolioDetailBankInstructionResponseDTO,
	ClientPortfolioInvestmentTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { DataRowCollapsible } from "web_myplann/Shared/Components/DataRowCollapsible";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";

type Props = {
	bankProducts: ClientPortfolioDetailBankInstructionResponseDTO[];
};

export const BankProductSection: FC<Props> = ({ bankProducts }) => {
	const { t } = useResource();

	const labelValue = useCallback(
		(bankProduct: ClientPortfolioDetailBankInstructionResponseDTO) => {
			const startAt = dateFormatter(bankProduct.startAt);

			return bankProduct.investmentType !==
				ClientPortfolioInvestmentTypeEnum.Empty
				? `${t(Resources.PortfolioDetail.ToolHistory.ActionTypeBankProduct[bankProduct.investmentType])} - ${startAt}`
				: startAt;
		},
		[t],
	);

	const recurringValue = useCallback(
		(bankProduct: ClientPortfolioDetailBankInstructionResponseDTO) => {
			const currency = bankProduct.currency
				? t(
						Resources.PortfolioDetail.SharedLabels.Currency[
							bankProduct.currency
						],
					)
				: t(Resources.Common.CurrencySign);

			const frequency = t(
				Resources.PortfolioDetail.ToolHistory.Fund.Frequency[
					bankProduct.frequency
				],
			).toLowerCase();

			return bankProduct.amount
				? `${moneyFormatter(bankProduct.amount, currency)} ${frequency}`
				: EMPTY_DETAIL_FIELD;
		},
		[t],
	);

	return (
		<Stack>
			{bankProducts.map((bankProduct, index) => {
				const startAt = dateFormatter(bankProduct.startAt);
				const endAt = dateFormatter(bankProduct.endAt);

				return (
					<DataRowCollapsible
						key={bankProduct.id}
						label={labelValue(bankProduct)}
						defaultExpanded={index === 0}
					>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.type)}
							value={t(
								Resources.PortfolioDetail.ToolHistory.ActionTypeBankProduct[
									bankProduct.investmentType
								],
							)}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.type)}
							value={""}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.startDate)}
							value={startAt}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.endDate)}
							value={endAt}
						/>
						<DataRow
							label={t(Resources.PortfolioDetail.ToolHistory.Fund.amount)}
							value={recurringValue(bankProduct)}
						/>
						<DataRow
							label={t(
								Resources.PortfolioDetail.ToolHistory.BankProducts.evaluation,
							)}
							value={
								bankProduct.interestRate ? `${bankProduct.interestRate} %` : ""
							}
						/>
					</DataRowCollapsible>
				);
			})}
		</Stack>
	);
};
